import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { TrelloIcon } from '@atlaskit/logo';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getLinksFromText } from '../../../../../../common/ui/link-attachments';
import { MentionsLinkedResults } from '../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { truncateTextWithQuery } from '../../../../../../common/utils/truncate-text-with-query';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchTrello_AtlassianSearchPage$key } from './__generated__/searchTrello_AtlassianSearchPage.graphql';

export type SearchTrelloProps = SearchResultProps<searchTrello_AtlassianSearchPage$key> & {
	grouping3pCohort?: string;
};

export const SearchTrello = ({
	node,
	position,
	cloudId,
	searchQuery,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
}: SearchTrelloProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchTrello_AtlassianSearchPage on SearchResult {
				__typename
				description
				id
				title
				type
				url
				... on SearchResultTrelloBoard {
					lastModifiedDate
				}
				... on SearchResultTrelloCard {
					lastModifiedDate
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type === 'issue' ? 'card' : data.type,
			entityType: data.type === 'issue' ? 'card' : data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const onResultClicked = useFireResultClicked({ metadata, position });

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(data.title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return data.title;
		}
		return enrichExcerpt(highlightedTitle);
	}, [data.title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = truncateTextWithQuery(data.description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [data.description, searchQuery]);

	const headerMetadataParts = [
		['type', <SearchResultType type={metadata.entityType as SearchClientResultType} />],
		[
			'lastModified',
			data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
		],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));
	const extractedLinksFromText = getLinksFromText(data.description);
	const hasExtractedLinks = extractedLinksFromText && extractedLinksFromText.length > 0;
	const hasRelatedLinks = (linkedResults && linkedResults.length > 0) || hasExtractedLinks;

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Trello}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			icon={<TrelloIcon size="medium" />}
			title={enrichedTitle}
			headerMetadataParts={headerMetadataParts}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			hasRelatedLinks={hasRelatedLinks}
			footer={
				<MentionsLinkedResults
					linkedResults={linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={data}
					url={data.url}
					position={position}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					isAllProductSelected={!selectedProduct}
				/>
			}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};

export const isSearchTrello = (node?: any): node is searchTrello_AtlassianSearchPage$key => {
	return (
		node?.__typename === 'SearchResultTrelloBoard' || node?.__typename === 'SearchResultTrelloCard'
	);
};
